import { createContext, useContext, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from './AuthContext'
import type { StatusMessage } from '../types/status'
import type { CreatePurchaseRequestPayload } from '../types/purchaseRequest'
import { createPurchaseRequest } from '../api/purchaseRequestsApi'
import { isHandledApiError } from '../api/httpClient'

export interface RequestsContextValue {
  status: StatusMessage | null
  listRefreshToken: number
  isRequestSubmitting: boolean
  setStatus: (status: StatusMessage | null) => void
  clearStatus: () => void
  triggerListRefresh: () => void
  createRequest: (payload: CreatePurchaseRequestPayload) => Promise<boolean>
  handleRequestUnauthorized: () => void
}

const RequestsContext = createContext<RequestsContextValue | undefined>(undefined)

interface RequestsProviderProps {
  children: ReactNode
}

export function RequestsProvider({ children }: RequestsProviderProps): ReactElement {
  const navigate = useNavigate()
  const { apiBaseUrl, handleUnauthorizedSession } = useAuthContext()
  const [status, setStatus] = useState<StatusMessage | null>(null)
  const [listRefreshToken, setListRefreshToken] = useState(0)

  const isRequestSubmitting = status?.kind === 'loading'

  function setStatusMessage(nextStatus: StatusMessage | null): void {
    setStatus(nextStatus)
  }

  function clearStatus(): void {
    setStatus(null)
  }

  function triggerListRefresh(): void {
    setListRefreshToken((prev) => prev + 1)
  }

  function handleRequestUnauthorized(): void {
    setStatus(null)
    handleUnauthorizedSession()
  }

  async function createRequest(payload: CreatePurchaseRequestPayload): Promise<boolean> {
    setStatus({ kind: 'loading', message: 'Отправка запроса...' })

    try {
      await createPurchaseRequest(apiBaseUrl, payload)
      setStatus({ kind: 'success', message: 'Заявка успешно создана.' })
      setListRefreshToken((prevToken) => prevToken + 1)
      navigate('/')
      return true
    } catch (error) {
      if (isHandledApiError(error)) {
        return false
      }

      const message = error instanceof Error ? error.message : 'Ошибка отправки запроса.'
      setStatus({ kind: 'error', message })
      return false
    }
  }

  const value = useMemo<RequestsContextValue>(() => ({
    status,
    listRefreshToken,
    isRequestSubmitting,
    setStatus: setStatusMessage,
    clearStatus,
    triggerListRefresh,
    createRequest,
    handleRequestUnauthorized
  }), [status, listRefreshToken, isRequestSubmitting])

  return (
    <RequestsContext.Provider value={value}>
      {children}
    </RequestsContext.Provider>
  )
}

export function useRequestsContext(): RequestsContextValue {
  const value = useContext(RequestsContext)
  if (!value) {
    throw new Error('useRequestsContext must be used inside RequestsProvider')
  }

  return value
}
